import { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../ui/alert-dialog';
import { MessageSquare, ChevronDown, ChevronUp, Search, Trash2 } from 'lucide-react';

const STATUS_OPTIONS = ['À contacter', 'En cours', 'Résolu'];

const STATUS_STYLES = {
  'À contacter': 'bg-amber-400/10 text-amber-400 border-amber-400/30',
  'En cours': 'bg-blue-400/10 text-blue-400 border-blue-400/30',
  'Résolu': 'bg-emerald-400/10 text-emerald-400 border-emerald-400/30',
};

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function AdminContactMessages({ messages, onStatusChange, onDelete }) {
  const [expandedId, setExpandedId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('Tous');
  const [search, setSearch] = useState('');

  const filtered = messages.filter(m => {
    if (filterStatus !== 'Tous' && m.status !== filterStatus) return false;
    if (search) {
      const q = search.toLowerCase();
      const name = `${m.firstName || ''} ${m.lastName || ''}`.toLowerCase();
      return name.includes(q) || (m.email || '').toLowerCase().includes(q) || (m.subject || '').toLowerCase().includes(q) || (m.message || '').toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div className="space-y-4" data-testid="admin-contact-messages">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="contact-msg-search"
            placeholder="Rechercher par nom, email, sujet..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 h-9"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px] h-9" data-testid="contact-msg-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Tous">Tous ({messages.length})</SelectItem>
            {STATUS_OPTIONS.map(s => (
              <SelectItem key={s} value={s}>{s} ({messages.filter(m => m.status === s).length})</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="p-12 text-center">
            <MessageSquare className="w-12 h-12 mx-auto mb-4 text-muted-foreground" strokeWidth={1} />
            <p className="text-muted-foreground">Aucune demande de contact.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden divide-y divide-border" data-testid="contact-msg-list">
          {filtered.map(m => {
            const isOpen = expandedId === m.request_id;
            const name = [m.firstName, m.lastName].filter(Boolean).join(' ') || '—';
            return (
              <div key={m.request_id} data-testid={`contact-msg-${m.request_id}`}>
                {/* Collapsed row */}
                <div
                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/30 transition-colors ${isOpen ? 'bg-muted/20' : ''}`}
                  onClick={() => setExpandedId(isOpen ? null : m.request_id)}
                  data-testid={`contact-msg-row-${m.request_id}`}
                >
                  <span className="text-xs text-muted-foreground w-20 shrink-0">{formatDate(m.created_at)}</span>
                  <span className="font-medium text-sm w-36 truncate shrink-0">{name}</span>
                  <span className="text-sm text-muted-foreground w-48 truncate shrink-0 hidden sm:block">{m.email}</span>
                  <span className="text-sm text-muted-foreground flex-1 truncate">{m.subject}</span>
                  <div className="shrink-0" onClick={e => e.stopPropagation()}>
                    <Select value={m.status || 'À contacter'} onValueChange={v => onStatusChange(m.request_id, v)}>
                      <SelectTrigger className={`h-7 text-xs border rounded-full px-2.5 w-[120px] ${STATUS_STYLES[m.status] || STATUS_STYLES['À contacter']}`} data-testid={`contact-msg-status-${m.request_id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="shrink-0" onClick={e => e.stopPropagation()}>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors" data-testid={`delete-msg-${m.request_id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Supprimer ce message ?</AlertDialogTitle>
                          <AlertDialogDescription>Le message de {name} sera définitivement supprimé.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annuler</AlertDialogCancel>
                          <AlertDialogAction onClick={() => onDelete(m.request_id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid={`confirm-delete-msg-${m.request_id}`}>Supprimer</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                  {isOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />}
                </div>

                {/* Expanded panel */}
                {isOpen && (
                  <div className="px-4 pb-4 pt-1 bg-muted/10 border-t border-border/50 animate-fade-in" data-testid={`contact-msg-detail-${m.request_id}`}>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm mb-3">
                      <div>
                        <span className="text-muted-foreground text-xs">Email</span>
                        <p><a href={`mailto:${m.email}`} className="text-primary hover:underline">{m.email}</a></p>
                      </div>
                      {m.profile && (
                        <div>
                          <span className="text-muted-foreground text-xs">Profil</span>
                          <p>{m.profile}</p>
                        </div>
                      )}
                      <div>
                        <span className="text-muted-foreground text-xs">Date</span>
                        <p>{m.created_at ? new Date(m.created_at).toLocaleString('fr-FR') : '—'}</p>
                      </div>
                    </div>
                    <div className="bg-background rounded-lg p-3 border border-border/50">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 font-semibold">Message</p>
                      <p className="text-sm whitespace-pre-wrap">{m.message}</p>
                    </div>
                    {m.reviewed_by && (
                      <p className="text-xs text-muted-foreground mt-2">Mis à jour par {m.reviewed_by}</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
